import {z} from 'zod';

const BootstrapPriceSchema = z.object({
  amount: z.number().nullable(),
  currency: z.string(),
});

export const ServiceCaseDetailBootstrapCaseSchema = z.object({
  id: z.string(),
  number: z.string(),
  state: z
    .enum(['DRAFT', 'CREATED', 'CANCELLED', 'HANDED_OVER', 'CHECKED_IN', 'PLANNED'])
    .nullable(),
  ordersState: z.string().nullish(),
  vehicleId: z.string(),
  customerId: z.string(),
  serviceCaseTypeId: z.string(),
  authorizationProfileId: z.string().nullish(),
  branchId: z.string().nullish(),
  markers: z.array(z.string().nullable()).nullish(),
  sparePartWarehouseState: z.string().nullish(),
  timeTrackingState: z.string().nullish(),
  createdAt: z.string(),
  bookingUrl: z.string().nullish(),
  repairUrl: z.string().nullish(),
});
export type ServiceCaseDetailBootstrapCase = z.infer<typeof ServiceCaseDetailBootstrapCaseSchema>;

// Option shape mirrors GetServiceCaseActionsApiResponseSchema so the slice can seed
// the getServiceCaseActions cache without reshaping.
export const ServiceCaseDetailBootstrapCaseActionsSchema = z.object({
  options: z
    .array(
      z
        .object({
          type: z.enum(['list-item', 'divider']).nullable(),
          key: z.enum(['cancel', 'reOpen']).nullable(),
          style: z.enum(['hidden', 'disabled', 'enable']).nullable(),
          position: z.number().nullable(),
        })
        .nullable()
    )
    .optional(),
});
export type ServiceCaseDetailBootstrapCaseActions = z.infer<
  typeof ServiceCaseDetailBootstrapCaseActionsSchema
>;

export const ServiceCaseDetailBootstrapIntegrationsSchema = z.object({
  codes: z.array(z.string()).nullish(),
  hasAnyEnabledAction: z.boolean().nullable(),
});
export type ServiceCaseDetailBootstrapIntegrations = z.infer<
  typeof ServiceCaseDetailBootstrapIntegrationsSchema
>;

export const ServiceCaseDetailBootstrapItemsTotalAmountSchema = z.object({
  totalPrice: z
    .object({
      withVat: BootstrapPriceSchema.nullish(),
      withoutVat: BootstrapPriceSchema.nullish(),
    })
    .nullish(),
});
export type ServiceCaseDetailBootstrapItemsTotalAmount = z.infer<
  typeof ServiceCaseDetailBootstrapItemsTotalAmountSchema
>;

export const ServiceCaseDetailBootstrapOrderWithMarginUnderMinimumSchema = z.object({
  serviceOrderId: z.string(),
  number: z.string(),
});
export type ServiceCaseDetailBootstrapOrderWithMarginUnderMinimum = z.infer<
  typeof ServiceCaseDetailBootstrapOrderWithMarginUnderMinimumSchema
>;

// Variant shape mirrors GetServiceCaseOrderVariantsApiResponse so the slice can seed
// the getServiceCaseOrderVariants cache without reshaping (full contract since the BE
// widening: serviceOrderType feeds OrderCard's color stripe + ACL scopes, isDefault the
// CreateServiceCase preselect).
export const ServiceCaseDetailBootstrapOrderVariantSchema = z.object({
  serviceOrderVariantId: z.string(),
  name: z.string(),
  serviceOrderType: z
    .object({
      serviceOrderTypeId: z.string(),
      type: z.string(),
    })
    .nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
});
export type ServiceCaseDetailBootstrapOrderVariant = z.infer<
  typeof ServiceCaseDetailBootstrapOrderVariantSchema
>;

const BootstrapPendingServiceItemSchema = z
  .object({
    serviceOrderId: z.string(),
    serviceJobId: z.string(),
  })
  .nullable();

export const ServiceCaseDetailBootstrapPendingServiceItemsSchema = z.object({
  workBasket: z.array(BootstrapPendingServiceItemSchema),
  materialBasket: z.array(BootstrapPendingServiceItemSchema),
});
export type ServiceCaseDetailBootstrapPendingServiceItems = z.infer<
  typeof ServiceCaseDetailBootstrapPendingServiceItemsSchema
>;

// Item shape mirrors GetPostponeJobsApiResponse so the slice can seed the
// getPostponeJobs cache without reshaping.
export const ServiceCaseDetailBootstrapPostponeJobsSchema = z.object({
  postponeJob: z
    .array(
      z
        .object({
          postponeJobId: z.string(),
          name: z.string().nullish(),
          postponedUntil: z.string().nullish(),
          postponeReason: z.string().nullish(),
          postponedAt: z.string(),
          postponedBy: z.string(),
        })
        .nullable()
    )
    .optional(),
  count: z.number().nullish(),
});
export type ServiceCaseDetailBootstrapPostponeJobs = z.infer<
  typeof ServiceCaseDetailBootstrapPostponeJobsSchema
>;

export const ServiceCaseDetailBootstrapCheckInSchema = z.object({
  state: z.enum(['SCHEDULED', 'COMPLETED']).nullish(),
  mileage: z.number().nullish(),
  fuelTank: z.number().nullish(),
  batteryCharge: z.number().nullish(),
  isVehicleNotInService: z.boolean().nullish(),
});
export type ServiceCaseDetailBootstrapCheckIn = z.infer<
  typeof ServiceCaseDetailBootstrapCheckInSchema
>;

export const ServiceCaseDetailBootstrapVehicleSchema = z.object({
  localActionNote: z.string().nullish(),
  vehicleCustomersCount: z.number().nullable(),
  activeServiceCasesCount: z.number().nullable(),
  finishedServiceCasesCount: z.number().nullable(),
});
export type ServiceCaseDetailBootstrapVehicle = z.infer<
  typeof ServiceCaseDetailBootstrapVehicleSchema
>;

// The aggregate inlines the Bosch fleet T&C document: null = not a Bosch fleet client
// (or the upstream failed), nil body = Bosch client without a T&C PDF (the granular
// probe's 404), body = base64-encoded document content.
export const ServiceCaseDetailBootstrapBoschFleetIntegrationSchema = z.object({
  contentType: z.string().nullish(),
  body: z.string().nullish(),
});
export type ServiceCaseDetailBootstrapBoschFleetIntegration = z.infer<
  typeof ServiceCaseDetailBootstrapBoschFleetIntegrationSchema
>;

export const ServiceCaseDetailBootstrapOperatorCustomerSchema = z.object({
  customerId: z.string(),
  displayName: z.string(),
});
export type ServiceCaseDetailBootstrapOperatorCustomer = z.infer<
  typeof ServiceCaseDetailBootstrapOperatorCustomerSchema
>;

// Every slice is nullable by contract: a failed upstream section returns null instead
// of failing the whole response (partial-failure semantics).
export const GetServiceCaseDetailBootstrapApiResponseSchema = z
  .object({
    case: ServiceCaseDetailBootstrapCaseSchema.nullable(),
    caseActions: ServiceCaseDetailBootstrapCaseActionsSchema.nullish(),
    additionalInformation: z.array(z.object({message: z.string()}).nullable()).nullish(),
    integrations: ServiceCaseDetailBootstrapIntegrationsSchema.nullish(),
    itemsTotalAmount: ServiceCaseDetailBootstrapItemsTotalAmountSchema.nullable(),
    orderCount: z.number().nullable(),
    ordersWithMarginUnderMinimum: z
      .array(ServiceCaseDetailBootstrapOrderWithMarginUnderMinimumSchema.nullable())
      .nullish(),
    orderVariants: z.array(ServiceCaseDetailBootstrapOrderVariantSchema.nullable()).nullable(),
    pendingServiceItems: ServiceCaseDetailBootstrapPendingServiceItemsSchema.nullish(),
    pendingServiceOrderReturn: z.object({serviceOrderReturnId: z.string()}).nullish(),
    postponeJobs: ServiceCaseDetailBootstrapPostponeJobsSchema.nullable(),
    checkIn: ServiceCaseDetailBootstrapCheckInSchema.nullish(),
    vehicle: ServiceCaseDetailBootstrapVehicleSchema.nullable(),
    activeServiceCampaignCount: z.number().nullable(),
    tireSetCount: z.number().nullable(),
    boschFleetIntegration: ServiceCaseDetailBootstrapBoschFleetIntegrationSchema.nullish(),
    operatorCustomer: ServiceCaseDetailBootstrapOperatorCustomerSchema.nullish(),
  })
  .nullish();
/** `undefined` covers the 204 (empty body) response. */
export type GetServiceCaseDetailBootstrapApiResponse = z.infer<
  typeof GetServiceCaseDetailBootstrapApiResponseSchema
>;

export const GetServiceCaseDetailBootstrapApiArgSchema = z.object({
  serviceCaseId: z.string(),
});
export type GetServiceCaseDetailBootstrapApiArg = z.infer<
  typeof GetServiceCaseDetailBootstrapApiArgSchema
>;
